import { Box, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import type { VoiceSpan } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

interface ReactionChipProps {
  span: VoiceSpan;
  isSelected: boolean;
  isReadOnly?: boolean;
  readOnlyTooltip?: string;
  onClick: () => void;
}

/**
 * Inline chip rendered inside a VoiceSegmentNode in display mode.
 * Represents a reaction (ElevenLabs v3 audio tag) span.
 */
export const ReactionChip = ({
  span,
  isSelected,
  isReadOnly = false,
  readOnlyTooltip,
  onClick,
}: ReactionChipProps): React.ReactNode => {
  const theme = useTheme();
  const { t } = useResource();

  const label = (() => {
    if (span.customReaction) return span.customReaction;
    if (span.reaction)
      return t(
        Resources.JobDetail.Reaction.Values[
          span.reaction as keyof typeof Resources.JobDetail.Reaction.Values
        ],
      );
    if (span.reactionVolumeEnergy)
      return t(
        Resources.JobDetail.Reaction.Values[
          span.reactionVolumeEnergy as keyof typeof Resources.JobDetail.Reaction.Values
        ],
      );
    if (span.reactionPacingRhythm)
      return t(
        Resources.JobDetail.Reaction.Values[
          span.reactionPacingRhythm as keyof typeof Resources.JobDetail.Reaction.Values
        ],
      );
    return t(Resources.JobDetail.Reaction.Heading);
  })();

  const accent = isReadOnly
    ? theme.palette.text.disabled
    : (theme.palette.secondary?.main ?? theme.palette.primary.main);

  const chip = (
    <Box
      component="span"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        mx: "2px",
        px: "6px",
        py: "1px",
        borderRadius: "10px",
        fontSize: "11px",
        fontWeight: 500,
        lineHeight: 1.4,
        cursor: isReadOnly ? "help" : "pointer",
        userSelect: "none",
        border: `1px solid ${accent}`,
        color: accent,
        backgroundColor: isSelected
          ? `${accent}33`
          : isReadOnly
            ? "transparent"
            : `${accent}14`,
        transition: "background-color 120ms",
        "&:hover": {
          backgroundColor: isReadOnly ? "transparent" : `${accent}22`,
        },
      }}
    >
      <span aria-hidden="true" style={{ fontSize: "10px" }}>
        ◆
      </span>
      {label}
    </Box>
  );

  const tooltip = isReadOnly
    ? (readOnlyTooltip ?? t(Resources.JobDetail.ReactionOnlyV3Hint))
    : `[${label}]`;

  return (
    <Tooltip title={tooltip} placement="top">
      {chip}
    </Tooltip>
  );
};
